import { useAnimalChanges } from '@/api/hooks';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDateTime } from '@/lib/format';
import type { AnimalChangeItem, AnimalChangeLogEntry } from '@/types/api';
import { ClipboardPen } from 'lucide-react';

export function AnimalChangesPanel({ animalId }: { animalId: number }) {
  const { data, isLoading, isError } = useAnimalChanges(animalId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 rounded-lg bg-manejai-sand/40 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={ClipboardPen}
        title="Histórico indisponível"
        description="Não foi possível carregar as alterações. A API pode precisar de atualização."
      />
    );
  }

  if (!data?.length) {
    return (
      <EmptyState
        icon={ClipboardPen}
        title="Nenhuma alteração registrada"
        description="Quando alguém cadastrar ou editar este animal, o histórico aparecerá aqui."
      />
    );
  }

  return (
    <div className="divide-y divide-manejai-sand/60 rounded-xl border border-manejai-sand/70">
      {data.map((entry) => (
        <ChangeEntry key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function ChangeEntry({ entry }: { entry: AnimalChangeLogEntry }) {
  const actor = entry.user_name ?? entry.user_email ?? 'Usuário desconhecido';
  const details = formatEntryDetails(entry);

  return (
    <p className="px-3 py-2.5 text-sm leading-snug text-manejai-bark">
      <time className="whitespace-nowrap text-manejai-bark/55">{formatDateTime(entry.created_at)}</time>
      <span className="text-manejai-bark/35"> · </span>
      <span className="whitespace-nowrap text-manejai-bark/75">{actor}</span>
      <span className="text-manejai-bark/35"> · </span>
      <span className="text-manejai-forest">{details}</span>
    </p>
  );
}

function formatEntryDetails(entry: AnimalChangeLogEntry): string {
  if (entry.changes.length === 0) {
    return entry.action_label;
  }

  return entry.changes.map(formatChangeItem).join(', ');
}

function formatChangeItem(change: AnimalChangeItem): string {
  if (change.from == null && change.to != null) {
    return `${change.label}: ${change.to}`;
  }

  if (change.from != null && change.to == null) {
    return `${change.label}: ${change.from} → removido`;
  }

  return `${change.label}: ${change.from ?? '—'} → ${change.to ?? '—'}`;
}
